import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArenaLogo } from "../../../components/common/ArenaLogo";
import { passwordService } from "../../../services/auth/password.service";
import axios from "axios";

interface ApiErrorResponse {
  message: string;
  error: string;
  statusCode: number;
}

export const BrandVerifyOtp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const sessionId = searchParams.get("sessionId") || undefined;
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await passwordService.verifyOtp(
        email,
        otp,
        "brand",
        sessionId
      );
      navigate(`/brand/auth/reset-password?token=${response.resetToken}`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data as ApiErrorResponse;
        if (
          errorData.statusCode === 400 &&
          errorData.message === "Invalid OTP"
        ) {
          setError(
            "The verification code you entered is incorrect. Please try again."
          );
        } else {
          setError(errorData.message || "Failed to verify code");
        }
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError(null);

    try {
      if (sessionId) {
        // If sessionId is available, use the shop-specific endpoint
        await passwordService.forgotPasswordForShop(email, sessionId, "brand");
      } else {
        // Otherwise use the regular endpoint
        await passwordService.forgotPassword(email, "brand");
      }
      setResendCooldown(60);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const errorData = err.response.data as ApiErrorResponse;
        setError(errorData.message || "Failed to resend code");
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setIsResending(false);
    }
  };

  const renderError = () =>
    error && (
      <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg flex items-center">
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {error}
      </div>
    );

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-1/4 pt-4 md:pt-0 md:h-max">
      <div className="  h-full mx-auto flex flex-col items-center justify-center   md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">
        <ArenaLogo className="w-12 mb-8 border border-black mx-auto rounded-[12px] bg-[#f6f6f6]" />
        <div className="w-full flex items-center justify-center px-6">
          <div className="w-full">
            {renderError()}
            <div className="mb-8">
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
                Verify Code
              </h1>
              <p className="font-normal text-[14px] mt-4 tracking-normal text-center align-middle text-muted-foreground">
                Please enter the 6-digit code sent to {email}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="otp"
                  className="block text-sm font-medium text-muted-foreground"
                >
                  Verification Code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className={`mt-1 block w-full px-3 py-2 border ${
                    error?.includes("incorrect")
                      ? "border-red-300"
                      : "border-border"
                  } rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500`}
                  required
                  maxLength={6}
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isResending}
                  className="text-sm text-brand-600 hover:text-brand-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-t-2 border-brand-600 rounded-full animate-spin mr-2" />
                      Resending...
                    </span>
                  ) : resendCooldown > 0 ? (
                    `Resend code in ${resendCooldown}s`
                  ) : (
                    "Resend code"
                  )}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
                ) : null}
                <span>Verify Code</span>
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/auth/login"
                className="text-sm text-brand-600 hover:text-brand-500 font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
